"use client";

import { fadeIn, staggerContainer } from "@/lib/motion";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from "next/image";

const GuideSection = () => {
  const t = useTranslations("guide");
  const clientJourney = t.raw("clientSteps");
  const consultantJourney = t.raw("consultantSteps");

  return (
    <motion.section
      variants={staggerContainer(0.1, 0.2)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: false, amount: 0.25 }}
      className="py-20 px-4 my-gradient-bg"
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          variants={fadeIn("down", "spring", 0.2, 1)}
          className="text-3xl md:text-4xl font-bold text-center mb-16 text-[var(--color-dark-navy)]"
        >
          {t("heading")}
        </motion.h2>

        {/* Client Journey */}
        <JourneySection
          title={t("clientJourneyTitle")}
          steps={clientJourney}
          accentColor="var(--color-olive-dark)"
          imageSrc="/imgs/agency.jpeg"
        />

        {/* Consultant Journey */}
        <JourneySection
          title={t("consultantJourneyTitle")}
          steps={consultantJourney}
          accentColor="var(--color-gold-bright)"
          reverseLayout
          imageSrc="/imgs/freelancer.jpeg"
        />
      </div>
    </motion.section>
  );
};

const JourneySection = ({
  title,
  steps,
  accentColor,
  reverseLayout = false,
  imageSrc,
}: {
  title: string;
  steps: string[];
  accentColor: string;
  reverseLayout?: boolean;
  imageSrc: string;
}) => (
  <motion.div
    variants={fadeIn("up", "spring", 0.4, 1)}
    className={`mb-24 ${reverseLayout ? "md:flex-row-reverse" : ""} flex flex-col md:flex-row gap-12`}
  >
    {/* Text + Timeline */}
    <div className="md:w-1/2">
      <h3
        className="text-2xl font-bold mb-8 p-4 rounded-lg shadow-md"
        style={{
          backgroundColor: accentColor,
          color: "var(--color-white)",
          boxShadow: `0 4px 20px ${accentColor}40`,
        }}
      >
        {title}
      </h3>

      <div className="relative">
        {/* Timeline Line */}
        <div
          className="absolute left-6 top-4 bottom-4 w-1 rounded-full"
          style={{ backgroundColor: `${accentColor}30` }}
        />

        {/* Steps */}
        <div className="space-y-8 pl-12">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={fadeIn("right", "spring", index * 0.2, 0.75)}
              className="flex items-start gap-4"
            >
              <div
                className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                style={{
                  backgroundColor: accentColor,
                  boxShadow: `0 4px 12px ${accentColor}80`,
                }}
              >
                {index + 1}
              </div>

              <div
                className="bg-white p-5 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg"
                style={{ borderLeft: `4px solid ${accentColor}` }}
              >
                <p className="text-[var(--color-gray-light)] text-lg">{step}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>

    {/* Image Section */}
    <div className="md:w-1/2 flex items-center justify-center">
      <div className="relative w-full h-80 rounded-2xl shadow-xl overflow-hidden">
        {/* Image */}
        <Image
          src={imageSrc}
          alt={title}
          fill
          className="object-cover"
        />

        {/* Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${accentColor}90 0%, ${accentColor}60 100%)`,
          }}
        />

      </div>
    </div>
  </motion.div>
);

export default GuideSection;